import { Controller } from "stimulus"

/**
 * Polymorphic applicant lookup for reservation applications.
 * Searches for businesses and contacts and sets hidden fields for
 * linked_entity_id and linked_entity_type.
 *
 * Targets:
 *   - searchInput: Text input for searching
 *   - searchArea: Container for the search input (hidden when entity selected)
 *   - results: Dropdown container for search results
 *   - selectedDisplay: Shows currently selected entity
 *   - entityName: Span showing the selected entity name
 *   - entityType: Span showing the selected entity type label
 *   - entityId: Hidden field for linked_entity_id
 *   - entityTypeField: Hidden field for linked_entity_type
 */
export default class extends Controller {
  static targets = [
    "searchInput", "searchArea", "results",
    "selectedDisplay", "entityName", "entityType",
    "entityId", "entityTypeField"
  ]

  connect() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
    }
  }

  search(e) {
    if (e.key === "Enter") { e.preventDefault(); return }
    if (e.key === "Escape") {
      this.resultsTarget.classList.add("hidden")
      return
    }

    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 3) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.searchTimeout = setTimeout(() => {
      fetch("/search/query.json?search=" + encodeURIComponent(query) + "&polymorphic=Business,Contact&limit=5")
        .then(response => response.json())
        .then(results => this.renderResults(results))
        .catch(error => console.error("Applicant search error:", error))
    }, 300)
  }

  renderResults(results) {
    const typeMap = { businesses: "Business", contacts: "Contact" }
    const container = this.resultsTarget
    container.innerHTML = ""

    let hasResults = false
    Object.keys(typeMap).forEach(type => {
      const items = results[type]
      if (items && items.length > 0) {
        hasResults = true

        const header = document.createElement("div")
        header.className = "px-4 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100 bg-gray-50"
        header.textContent = typeMap[type]
        container.appendChild(header)

        items.forEach(item => {
          const btn = document.createElement("button")
          btn.type = "button"
          btn.className = "block w-full text-left px-4 py-2 text-gray-900 bg-white hover:bg-purple-50 cursor-pointer text-sm"
          btn.textContent = this.displayName(type, item)
          btn.dataset.entityId = item.id
          btn.dataset.entityType = typeMap[type]
          btn.addEventListener("click", (e) => this.selectEntity(e))
          container.appendChild(btn)
        })
      }
    })

    if (!hasResults) {
      container.innerHTML = '<div class="px-4 py-2 text-sm text-gray-500 bg-white">No results found</div>'
    }

    container.classList.remove("hidden")
  }

  displayName(type, item) {
    if (type === "businesses") return item.business_name || ""
    if (type === "contacts") return item.name_search_string || ""
    return item.name || String(item.id)
  }

  selectEntity(e) {
    const el = e.currentTarget
    const id = el.dataset.entityId
    const type = el.dataset.entityType

    this.entityIdTarget.value = id
    this.entityTypeFieldTarget.value = type

    this.searchInputTarget.value = ""
    this.resultsTarget.classList.add("hidden")

    this.selectedDisplayTarget.classList.remove("hidden")
    this.entityNameTarget.textContent = el.textContent.trim()
    this.entityTypeTarget.textContent = "(" + type + ")"

    this.searchAreaTarget.classList.add("hidden")
  }

  clear(e) {
    e.preventDefault()
    this.entityIdTarget.value = ""
    this.entityTypeFieldTarget.value = ""
    this.searchInputTarget.value = ""
    this.resultsTarget.classList.add("hidden")
    this.selectedDisplayTarget.classList.add("hidden")
    this.entityNameTarget.textContent = ""
    this.entityTypeTarget.textContent = ""
    this.searchAreaTarget.classList.remove("hidden")
  }

  closeResults(e) {
    if (this.hasResultsTarget && !this.element.contains(e.target)) {
      this.resultsTarget.classList.add("hidden")
    }
  }
}
